import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Building, ArrowRight, CheckCircle, Star, Flame, Archive } from 'lucide-react';
import { programs } from '../data/programs';

// Re-export for detail page
export { programs };

// Filter only active programs
const activePrograms = programs.filter(p => p.status === 'active');
const allTags = [...new Set(activePrograms.flatMap(p => p.tags))];

const CorporatePage = () => {
  const [selectedTags, setSelectedTags] = useState([]);

  const toggleTag = (tag) => {
    setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  const filteredPrograms = selectedTags.length === 0 ? activePrograms : activePrograms.filter(p => p.tags.some(tag => selectedTags.includes(tag)));

  return (
    <div className="min-h-screen pt-20" data-testid="corporate-page">
      {/* Hero */}
      <section className="py-12 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="hero-glow top-0 left-1/4" />
        <div className="container-main relative z-10">
          <div className="max-w-3xl">
            <Link to="/services" className="inline-flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--primary)] mb-6 font-mono text-sm">
              <ArrowRight className="w-4 h-4 rotate-180" /> Back to Services
            </Link>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 border border-[var(--accent-purple)] bg-[var(--accent-purple)]/10 flex items-center justify-center">
                <Building className="w-8 h-8 text-[var(--accent-purple)]" />
              </div>
              <div>
                <p className="font-mono text-sm uppercase tracking-wider text-[var(--accent-purple)]">Enterprise Solutions</p>
                <h1 className="text-3xl md:text-5xl font-bold text-[var(--text-main)]">Corporate Training</h1>
              </div>
            </div>
            <p className="text-[var(--text-muted)] text-lg md:text-xl leading-relaxed">
              Customized cybersecurity training programs for organizations. From security awareness to technical deep-dives. Our mission at Byt3con is simple "Learn by Doing" a fully hands on outcome driven practictioner led trainings.
            </p>
            {/* <Link to="/services/corporate/archive" className="inline-flex items-center gap-2 mt-6 text-[var(--text-muted)] hover:text-[var(--accent-purple)] font-mono text-sm border border-[var(--border)] px-4 py-2 hover:border-[var(--accent-purple)] transition-colors" data-testid="view-archive-link">
              <Archive className="w-4 h-4" /> View Past Programs
            </Link> */}
          </div>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 border-y border-[var(--border)] ">
        <div className="container-main">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <span className="font-mono text-sm text-[var(--accent-purple)] uppercase tracking-wider">Filter by Tag:</span>
            <div className="flex flex-wrap gap-2">
              {allTags.map((tag) => (
                <button key={tag} onClick={() => toggleTag(tag)} className={`px-4 py-2 font-mono text-sm border transition-colors duration-200 ${selectedTags.includes(tag) ? 'border-[var(--accent-purple)] bg-[var(--accent-purple)]/20 text-[var(--accent-purple)]' : 'border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--accent-purple)]/50'}`} data-testid={`tag-${tag.toLowerCase().replace(/\s+/g, '-')}`}>{tag}</button>
              ))}
              {selectedTags.length > 0 && (
                <button onClick={() => setSelectedTags([])} className="px-4 py-2 font-mono text-sm border border-[var(--error)]/50 text-[var(--error)] hover:bg-[var(--error)]/10">Clear All</button>
              )}
            </div>
          </div>
          <p className="mt-4 text-sm text-[var(--text-muted)]">Showing {filteredPrograms.length} of {activePrograms.length} programs</p>
        </div>
      </section>

      {/* Programs */}
      <section className="py-16">
        <div className="container-main">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {filteredPrograms.map((program) => (
              <div key={program.id} className="bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--accent-purple)] transition-colors duration-300 overflow-hidden group relative" data-testid={`program-card-${program.id}`}>
                {/* Featured & Popular Badges */}
                <div className="absolute top-4 right-4 flex gap-2 z-10">
                  {program.featured && (
                    <span className="flex items-center gap-1 px-2 py-1 bg-[#FFD700]/20 border border-[#FFD700] text-[#FFD700] text-xs font-mono uppercase" data-testid={`featured-badge-${program.id}`}>
                      <Star className="w-3 h-3 fill-[#FFD700]" /> Featured
                    </span>
                  )}
                  {program.popular && (
                    <span className="flex items-center gap-1 px-2 py-1 bg-[#FF6B00]/20 border border-[#FF6B00] text-[#FF6B00] text-xs font-mono uppercase" data-testid={`popular-badge-${program.id}`}>
                      <Flame className="w-3 h-3" /> Popular
                    </span>
                  )}
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-[var(--text-main)] group-hover:text-[var(--accent-purple)] transition-colors mb-3 mt-6">{program.name}</h3>
                  <p className="text-[var(--text-muted)] leading-relaxed mb-6">{program.description}</p>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[var(--accent-purple)]" /><div><p className="text-xs text-[var(--text-muted)]">Duration</p><p className="text-sm text-[var(--text-main)] font-mono">{program.duration}</p></div></div>
                    <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[var(--accent-purple)]" /><div><p className="text-xs text-[var(--text-muted)]">Location</p><p className="text-sm text-[var(--text-main)] font-mono">{program.format}</p></div></div>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {program.tags.map((tag, idx) => (<span key={idx} className="px-2 py-1 bg-[var(--surface-highlight)] border border-[var(--border)] text-xs text-[var(--text-muted)] font-mono">{tag}</span>))}
                  </div>
                  <Link to={`/services/corporate/${program.id}`} className="w-full bg-[var(--primary)] text-black font-mono font-bold px-6 py-4 uppercase tracking-widest hover:bg-[var(--primary-dim)] transition-colors duration-200 shadow-[0_0_15px_var(--glow-primary)] text-center block" data-testid={`know-more-btn-${program.id}`}>Know More</Link>
                </div>
              </div>
            ))}
          </div>
          {filteredPrograms.length === 0 && (
            <div className="text-center py-16">
              <p className="text-[var(--text-muted)] text-lg">No programs match the selected filters.</p>
              <button onClick={() => setSelectedTags([])} className="mt-4 text-[var(--accent-purple)] font-mono hover:underline">Clear filters</button>
            </div>
          )}
        </div>
      </section>
      <div className="container-main flex justify-center mb-12">
        <Link to="/services/corporate/archive" className="inline-flex items-center gap-2 mt-6 text-[var(--text-muted)] hover:text-[var(--accent-purple)] font-mono text-sm border border-[var(--border)] px-4 py-2 hover:border-[var(--accent-purple)] transition-colors" data-testid="view-archive-link">
          <Archive className="w-4 h-4" /> View Past Programs
        </Link>
      </div>
    </div>
  );
};

export default CorporatePage;
